import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Chinese mirror of the Thai spouse-visa document checklist guide. */
const TITLE = "配偶签证泰国文件清单 — 认证顺序与常见退件原因";
const DESC =
  "配偶／伴侣签证需要哪些泰国文件、翻译与认证的正确顺序，以及最常导致拒签或退件的错误。";
const PATH = "/blog/spouse-visa-document-checklist";
const URL = `/zh${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "配偶签证通常需要哪些泰国文件？",
    answer:
      "常见为结婚登记证书、婚姻状况登记记录、身份证与户口簿、护照、出生证明（涉及子女时）、必要时的品行证明，以及共同生活的证明材料。具体清单以受理机关公告为准。",
  },
  {
    question: "文件需要翻译成哪种语言？",
    answer:
      "以受理机关接受的语言为准，多数为英语，部分国家要求本国语言。译文须完整对应原件，包括印章、钢印与批注文字。",
  },
  {
    question: "认证顺序应该怎么安排？",
    answer:
      "一般顺序为：取得原件或认证副本 → 私文书由公证律师认证 → 依需要经外交部领事司认证 → 完整翻译 → 目的地使领馆认证或附加证明书。顺序颠倒是常见的退件原因。",
  },
  {
    question: "文件有有效期限制吗？",
    answer:
      "许多机关要求登记类文件为近一至六个月内签发。由于认证需要工作日，建议从递交截止日往前倒推安排，而非尽早申请。",
  },
  {
    question: "在国外结婚的登记需要在泰国登录吗？",
    answer:
      "视用途而定。若须向泰国机关证明婚姻状况，通常需要办理相应的登记或记录；若仅向目的地机关递交，则以该机关要求为准。",
  },
  {
    question: "可以委托他人代办吗？",
    answer:
      "可以，但授权委托书的权限须涵盖申请、领取、送认证与递交等每一环节。权限过窄会导致流程中断。",
  },
  {
    question: "最常见的拒签原因有哪些？",
    answer:
      "姓名拼写不一致、译文不完整、认证顺序错误、文件过期、关系证明材料薄弱，以及提交复印件而非原件。",
  },
];

export const Route = createFileRoute("/zh/blog/spouse-visa-document-checklist")({
  component: ZhSpouseVisaChecklist,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "zh" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("spouse-visa-document-checklist", "zh") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "配偶签证 泰国文件, 结婚证书 翻译认证, 泰国结婚登记 认证, 伴侣签证 文件清单, 泰国文件 使领馆认证",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "zh"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "首页", url: "/zh" },
              { name: "指南", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("spouse-visa-document-checklist"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Visa & Immigration",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ doc: string; note: string }> = [
  { doc: "结婚登记证书", note: "须为登记机关签发的原件或认证副本，姓名拼写与护照一致。" },
  { doc: "婚姻状况登记记录", note: "证明当前婚姻状况，部分机关要求近期签发。" },
  { doc: "身份证与户口簿", note: "地址记录须为最新，与其他文件相符。" },
  { doc: "护照", note: "有效期须覆盖递交与审理期间。" },
  { doc: "出生证明（涉及子女）", note: "亲子关系文件须完整翻译，含登记机关批注。" },
  { doc: "品行证明（视要求）", note: "签发日期须落在目的地的有效期窗口内。" },
];

const SEQUENCE: string[] = [
  "取得受理机关的最新文件清单与语言要求",
  "取得原件或认证副本",
  "私文书由公证律师认证签名",
  "依需要办理外交部领事司认证",
  "完整翻译，含印章与批注文字",
  "办理使领馆认证或附加证明书，并核对全套文件后递交",
];

const MISTAKES: string[] = [
  "姓名在不同文件中拼写不一致",
  "译文遗漏印章、钢印或登记批注",
  "先翻译后认证，认证未出现在译文中",
  "登记类文件签发过早已超出有效期窗口",
  "关系证明材料不足（共同居住、往来记录等）",
  "以复印件替代原件递交",
];

function ZhSpouseVisaChecklist() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/zh" className="text-muted-foreground hover:text-foreground">
          首页
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          指南
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="spouse-visa-document-checklist" locale="zh" />
      <p className="answer-summary speakable-summary">
        配偶签证被拒，多数并非因为材料不足，而是姓名拼写不一致、译文不完整或认证顺序错误。
        建议从递交截止日往前倒推：先确认清单与语言要求，再依原件、公证、领事司、翻译、使领馆的顺序完成。
      </p>

      <h2>1. 文件清单</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d.doc}>
            <strong>{d.doc}：</strong> {d.note}
          </li>
        ))}
      </ul>

      <h2>2. 认证顺序</h2>
      <ol>
        {SEQUENCE.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 时间规划</h2>
      <p>
        登记类文件常被要求为近一至六个月内签发，而公证、领事司与使领馆各需工作日。
        稳妥做法是从递交截止日倒推，为每一环节预留时间，避免文件在流程结束前失效。
      </p>

      <h2>4. 常见退件原因</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>5. 常见问题</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>6. 参考来源与相关服务</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          泰国外交部领事司
        </a>{" "}
        ·{" "}
        <a href="https://www.bora.dopa.go.th/" target="_blank" rel="noreferrer">
          泰国内政部地方行政登记局
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/zh/marriage" className="text-primary underline">
            婚姻与家庭文件服务
          </Link>
        </li>
        <li>
          <Link to="/zh/visa" className="text-primary underline">
            签证与移民文件服务
          </Link>
        </li>
        <li>
          <Link to="/zh/lgz" className="text-primary underline">
            文件认证与领事认证服务
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容复核日期 {PUBLISHED}。本指南仅为规划用的一般信息，不代表任何机关的决定。办理前请向受理机关确认最新要求。
      </p>
    </article>
  );
}
